import React, { createContext, useContext, useMemo, useReducer } from "react";
import globalEventBus from "../utils/EventBus";
import { UserSchema } from "../realm/contactSchema";
import { CHAT_LIST, ChatListSchema, MessageSchema } from "../realm/schema";
import { ChartMessageItem } from "../constants/types";
import {
    batchUpdateObjArr,
    clean_msg_by_tid, del_topic, delete_user_summary, handelBlack,
    replace_topic_summary,
    TopicSummaryStat,
    update_user,
    update_user_avatar,
    update_user_remark,
    upsert_topic_draft,
    upsert_topic_summary
} from "./utils";
import { TopicWithSummary } from "../../imsdk/api/topic";
import { FriendBlack } from "../../imsdk/db/t_friend_black";
import { User } from "../../imsdk/db/t_user";
import { Msg } from "../../imsdk/db/t_msg";
import { openNotify } from "../screen/Chat/ChatScreen";
import { LoginDevice, TenantSettings } from "../../imsdk/db/t_user_devices";

export interface UserState {
    topicSummaryList: TopicSummaryStat, // 会话所有信息 woo
    dataVersion: number, // 数据版本
    exampleCount: number, // 测试
    selectMessages: ChartMessageItem[], // 选中的消息
    contactList: User[], // 通讯录列表
    contactBlackList: FriendBlack[], // 通讯录列表
    user: UserSchema,
    chatList: ChatListSchema[], // 会话列表
    preloadMessages: ChartMessageItem[], // 用来预渲染会话的messages
    friendApply: number, //好友申请
    forwardSuccTip: boolean, // 转发成功提示
    latestVersion: string, // 当前最新的版本
    loginGuide?: boolean,
    loginDevices?: LoginDevice[], // 登录设备列表
    tenantSettings?: TenantSettings // 租户设置
}

export type Payload =
    | { type: 'INIT_STORE_DATA'; payload?: undefined | null }
    | { type: "UPDATE_TOPIC_DRAFT", payload: { tid: string, draft: string } }
    | { type: 'UPDATE_FRIEND_APPLY_NUM'; payload: number }
    | { type: 'UPDATE_TOPIC_SUMMARY'; payload: TopicWithSummary[] }
    | { type: 'UPDATE_USER_INFO'; payload: UserSchema }
    | { type: 'UPDATE_CHAT_LIST'; payload: ChatListSchema[]; }
    | { type: 'UPDATE_CURRENT_CHAT'; payload: ChatListSchema; }
    | { type: 'UPDATE_CHAT_LIST_ITEM'; payload: { tid: string; chat: ChatListSchema }; }
    | { type: 'UPDATE_FRIEND_APPLY'; payload: number; }
    | { type: 'UPDATE_PRELOAD_MSG'; payload: MessageSchema[]; }
    | { type: 'UPDATE_FORWARD_TIP_STATUS'; payload: boolean; }

    | { type: 'BATCH_UPDATE_CHAT_ITEM'; payload: ChatListSchema[]; }
    | { type: 'BATCH_DELETE_CHAT'; payload: string[]; }

    | { type: 'UPDATE_SELECT_MESSAGE'; payload: ChartMessageItem[]; }
    | { type: 'LATEST_VERSION'; payload: string; }
    | { type: 'REPLACE_TOPIC_SUMMARY_LIST'; payload: TopicSummaryStat; }
    | { type: 'UPSERT_TOPIC_SUMMARY'; payload: TopicWithSummary[]; }
    | { type: 'CLEAN_MSG'; payload: string; }
    | { type: 'REPLACE_CONTACT_LIST'; payload: { users: User[], black: FriendBlack[]; } }
    | { type: 'UPDATE_CONTACT_LIST'; payload: User }
    | { type: 'UPDATE_CONTACT_REMARK'; payload: { uid: number, remark: string, tids: string[] } }
    | { type: 'UPDATE_CONTACT_AVATAR'; payload: { uid: number, avatar: string, tids: string[] } }
    | { type: 'UPDATE_CONTACT_BLACK_LIST'; payload?: { uid: number; black: string, time: number } }
    | { type: 'EXIT_GROUP'; payload: { tid: string } }
    | { type: 'LOGOUT'; payload?: undefined | null }
    | { type: 'Notify'; payload: Msg }
    | { type: 'DELETE_USER', payload: number }
    | { type: 'SET_LOGIN_GUIDE', payload: boolean }
    | { type: 'UPDATE_LOGIN_DEVICES', payload: LoginDevice[] }
    | { type: 'UPDATE_TENANT_SETTINGS', payload: TenantSettings }




const reducer = (state: UserState, { type, payload }: Payload): UserState => {
    let nextState = state;
    let emitEvent = null;
    switch (type) {
        case 'SET_LOGIN_GUIDE':
            return { ...state, loginGuide: payload };
        case 'UPDATE_LOGIN_DEVICES':
            return { ...state, loginDevices: payload };
        case 'UPDATE_TENANT_SETTINGS':
            return { ...state, tenantSettings: payload };
        case 'Notify':
            openNotify(payload, state.topicSummaryList)
            return state;

        case "UPDATE_TOPIC_DRAFT":
            if (upsert_topic_draft(state.topicSummaryList, payload.tid, payload.draft)) {
                return { ...state, dataVersion: state.dataVersion + 1 };
            }
            return state
        case 'DELETE_USER':
            if (delete_user_summary(state, payload)) {
                return { ...state, dataVersion: state.dataVersion + 1 };
            }
            return state
        case 'LOGOUT':
            return { ...initState, chatList: [], dataVersion: state.dataVersion + 1 };

        case 'REPLACE_TOPIC_SUMMARY_LIST':
            const newTopicSummary = replace_topic_summary(payload);
            return {
                ...state,
                dataVersion: state.dataVersion + 1,
                topicSummaryList: newTopicSummary
            };
        case 'REPLACE_CONTACT_LIST':
            return {
                ...state,
                dataVersion: state.dataVersion + 1,
                contactList: payload.users,
                contactBlackList: payload.black
            };
        case 'UPDATE_CONTACT_REMARK': {
            if (update_user_remark(payload.uid, payload.remark, payload.tids, state)) {
                nextState = { ...state, dataVersion: state.dataVersion + 1 };
                emitEvent = { type: 'Set_Remark_USER', data: { user_remark: { uid: payload.uid, remark: payload.remark } } };
            }
            break;
        }
        case 'UPDATE_CONTACT_AVATAR': {
            if (update_user_avatar(payload.uid, payload.avatar, payload.tids, state)) {
                nextState = { ...state, dataVersion: state.dataVersion + 1 };
                emitEvent = { type: 'Set_Avatar_USER', data: { user_avatar: { uid: payload.uid, avatar: payload.avatar } } };
            }
            break;
        }
        case 'UPDATE_CONTACT_LIST': {
            if (update_user(payload, state.contactList,)) {
                nextState = { ...state, dataVersion: state.dataVersion + 1 };
                emitEvent = { type: 'Set_UserInfo_USER', data: { user: payload } };
            }
            break;
        }
        case 'UPDATE_CONTACT_BLACK_LIST':
            if (!payload) {
                return { ...state }
            }
            const { uid, black, time } = payload
            const blacks = handelBlack(state.contactBlackList, uid, black, time)
            return {
                ...state,
                dataVersion: state.dataVersion + 1,
                contactBlackList: blacks
            };
        case 'UPSERT_TOPIC_SUMMARY':
            if (
                upsert_topic_summary(
                    state.topicSummaryList,
                    payload,
                    true
                )
            ) {
                return { ...state, dataVersion: state.dataVersion + 1 };
            }
            return state;
        case 'CLEAN_MSG':
            if (
                clean_msg_by_tid(
                    state.topicSummaryList,
                    payload
                )
            ) {
                return { ...state, dataVersion: state.dataVersion + 1 };
            }
            return state;
        case 'UPDATE_TOPIC_SUMMARY':
            if (
                upsert_topic_summary(
                    state.topicSummaryList,
                    payload,
                    false
                )
            ) {
                return { ...state, dataVersion: state.dataVersion + 1 };
            }
            return state;
        // 添加userInfo
        case 'UPDATE_USER_INFO':
            return {
                ...state,
                user: payload
            };
        // 更新选中
        case 'UPDATE_SELECT_MESSAGE':
            return {
                ...state,
                selectMessages: payload
            };
        // 更新会话列表
        case 'UPDATE_CHAT_LIST':
            return {
                ...state,
                chatList: payload
            };
        // 更新当前会话列表中的某条
        case 'UPDATE_CHAT_LIST_ITEM':
            return {
                ...state,
                chatList: state.chatList.map((item) =>
                    item.tid === payload.tid ? payload.chat : item
                )
            };
        case 'UPDATE_FRIEND_APPLY_NUM':
            return {
                ...state,
                friendApply: payload
            };
        case 'UPDATE_FORWARD_TIP_STATUS':
            return {
                ...state,
                forwardSuccTip: payload
            };
        // 预渲染的消息
        case 'UPDATE_PRELOAD_MSG':
            return {
                ...state,
                preloadMessages: payload
            };
        // 批量更新
        case 'BATCH_UPDATE_CHAT_ITEM':
            return {
                ...state,
                chatList: batchUpdateObjArr(state.chatList, payload, "tid")
            };
        case 'BATCH_DELETE_CHAT':
            return {
                ...state,
                chatList: state.chatList.map((item) => {
                    if (payload.includes(item.tid)) {
                        return { ...item, isDelete: true };
                    }
                    return item;
                })
            };

        case 'INIT_STORE_DATA':
            return {
                ...state,
                chatList: [],
            };
        case 'LATEST_VERSION':
            return {
                ...state,
                latestVersion: payload
            };
        case 'EXIT_GROUP':
            const summaryStat = del_topic(state.topicSummaryList, payload.tid,);
            return { ...state, topicSummaryList: summaryStat, dataVersion: state.dataVersion + 1 };
        default:
            return state;
    }
    // 统一事件派发
    if (emitEvent) {
        setTimeout(() => {
            if (emitEvent.type === 'Set_Remark_USER') {
                globalEventBus.emit('Set_Remark_USER', emitEvent.data);
            } else if (emitEvent.type === 'Set_Avatar_USER') {
                globalEventBus.emit('Set_Avatar_USER', emitEvent.data);
            } else if (emitEvent.type === 'Set_UserInfo_USER') {
                globalEventBus.emit('Set_UserInfo_USER', emitEvent.data);
            }
        }, 0);
    }
    return nextState;
};


// 初始化状态
const initState: UserState = {
    topicSummaryList: { top: [], normal: [] }, // 会话所有信息 woo
    dataVersion: 0, // 数据版本
    exampleCount: 0, // 测试
    selectMessages: [], // 选中的消息
    user: {} as UserSchema,
    chatList: [], // 会话列表
    preloadMessages: [], // 用来预渲染会话的messages
    friendApply: 0, //好友申请
    forwardSuccTip: false, // 转发成功提示
    latestVersion: "", // 当前最新的版本
    contactList: [], // 通讯录列表
    contactBlackList: [], // 通讯录列表
    loginDevices: [], // 登录设备列表
    tenantSettings: {} // 租户设置
};

interface ContextProps {
    state?: UserState,
    dispatch: React.Dispatch<Payload>
}

const UserContext = createContext<ContextProps>({
    state: initState, dispatch: () => {

    }
});


export function UserProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(reducer, initState);
    const contextValue = useMemo(() => {
        return { state, dispatch };
    }, [state, dispatch]);
    return (<UserContext.Provider value={contextValue}>{children}</UserContext.Provider>);
}

export const useStore = () => {
    return useContext(UserContext);
};
